import { Box, Flex, Grid, Heading, useDisclosure } from '@chakra-ui/react';
import { Image } from '@components/AssetWrapper';
import { APIKongVX, KollectionType } from '@cyberkongz/api-interfaces';
import { ChainType } from '@cyberkongz/config-blockchain';
import { Kong } from '@cyberkongz/database';
import {
  AddressInput,
  Card,
  FormButtonsEditing,
  FormButtonsWaiting4Confirmation,
  FormCompleted,
  FormStepDescription,
  FormStepsBar,
  FormSummaryIcon,
  FormWithTxStage,
  getAddressPath,
  getKongImageByType,
  KongActionIcon,
  Modal,
  TransferIcon,
  ViewInBlockExplorerLink,
  WithExpectedNetworkConnectionLegacy,
} from '@cyberkongz/ui';
import {
  formatAddressLong,
  getKongzContract,
  getKongzVXContract,
  getKongzVXPolygonContract,
  useChainEnhanced,
  useIsValidAddress,
  useKongVXEnhancedData,
  useWriteContractWithTracking,
} from '@cyberkongz/ui-blockchain';
import { useSharedUI } from '@features/sharedUI/SharedUIProvider';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAccount, useSimulateContract } from 'wagmi';

function FormSummary({
  id,
  type,
  sendTo,
  hideLink,
}: {
  id: number;
  type: KollectionType;
  sendTo: string;
  hideLink?: boolean;
}) {
  return (
    <Card>
      <Flex textStyle="p-sm" align="center" justify="space-between">
        <Grid gap={1} textStyle="p-sm" textAlign="center" fontWeight="medium">
          <Image
            borderRadius="sm"
            src={getKongImageByType(id, type)}
            css={{ w: 24, h: type === 'vx' ? 'auto' : 24 }}
            alt={`Kong ${id} preview`}
          />
          {type === 'genesis' ? 'Genesis' : type === 'baby' ? 'Baby' : 'VX'} {id}
        </Grid>
        <FormSummaryIcon icon={<TransferIcon width={6} height="auto" />} />
        <Grid>
          <Box>To: {formatAddressLong(sendTo)}</Box>
          {!hideLink ? <ViewInBlockExplorerLink path={getAddressPath(sendTo)} /> : null}
        </Grid>
      </Flex>
    </Card>
  );
}

interface TransferActionFormViewProps {
  stage: FormWithTxStage;
  sendTo: string;
  isValidAddress: boolean;
  id: number;
  type: KollectionType;
  txHash?: string;
  setStage: Dispatch<SetStateAction<FormWithTxStage>>;
  setSendTo: Dispatch<SetStateAction<string>>;
  writeContract?: () => void;
  reset: () => void;
  expectedNetwork: ChainType;
}

function TransferActionFormView({
  stage,
  sendTo,
  isValidAddress,
  id,
  type,
  txHash,
  setStage,
  setSendTo,
  writeContract,
  reset,
  expectedNetwork,
}: TransferActionFormViewProps) {
  const { open: isOpen, onClose, onOpen } = useDisclosure();
  const { chain } = useChainEnhanced();
  const { kongVXTheme } = useSharedUI();

  function closeModal() {
    reset();
    onClose();
  }

  return (
    <>
      <KongActionIcon label="Transfer" onClick={onOpen}>
        <TransferIcon color={kongVXTheme?.text} />
      </KongActionIcon>
      <Modal
        variant="form"
        {...{
          isOpen,
          onClose: closeModal,
          headerComponent:
            stage !== 'completed' ? <Heading size="xs">Transfer Kong</Heading> : null,
        }}
      >
        <WithExpectedNetworkConnectionLegacy expectedNetwork={expectedNetwork}>
          <Grid gap={8}>
            {stage !== 'completed' ? (
              <FormStepsBar totalSteps={2} currentStep={stage === 'editing' ? 1 : 2} />
            ) : null}
            {stage === 'editing' ? (
              <Grid gap={6}>
                <FormStepDescription
                  title={`1. ${chain?.name} address`}
                  description={`Please enter ${chain?.name} address you would like to transfer Kong to.`}
                />
                <AddressInput
                  value={sendTo}
                  setValue={setSendTo}
                  isValidAddress={isValidAddress}
                  label={`${chain?.name} Address:`}
                  errorMessage={`Provided ${chain?.name} address is invalid`}
                />
                <FormButtonsEditing
                  setStage={setStage}
                  onClickCancel={closeModal}
                  isDisabledNext={!writeContract}
                />
              </Grid>
            ) : stage === 'waiting4Confirmation' || stage === 'inProgress' ? (
              <Grid gap={6}>
                <FormStepDescription title="2. Confirm Transfer" />
                <FormSummary {...{ id, type, sendTo }} />
                <FormButtonsWaiting4Confirmation
                  stage={stage}
                  setStage={setStage}
                  onClickNext={writeContract}
                />
              </Grid>
            ) : (
              <FormCompleted
                {...{
                  onClose: closeModal,
                  txHash,
                  title: 'Transfer completed',
                  summary: <FormSummary {...{ id, type, sendTo, hideLink: true }} />,
                }}
              />
            )}
          </Grid>
        </WithExpectedNetworkConnectionLegacy>
      </Modal>
    </>
  );
}

export function TransferActionForm({
  kong,
  type,
}: {
  kong: Kong | APIKongVX;
  type: KollectionType;
}) {
  const { id } = kong;
  const isVX = type === 'vx';

  const [stage, setStage] = useState<FormWithTxStage>('editing');
  const [sendTo, setSendTo] = useState('');
  const { address } = useAccount();
  const { chain } = useChainEnhanced();
  const { chain: vxChain } = useKongVXEnhancedData(isVX ? (kong as APIKongVX) : undefined);

  const expectedNetwork = isVX && vxChain ? vxChain : 'mainnet';
  const isValidAddress = useIsValidAddress(sendTo);

  const { data: dataSimulateKongz } = useSimulateContract({
    ...getKongzContract(chain),
    functionName: 'transferFrom',
    args: [address!, sendTo as `0x${string}`, BigInt(id)],
    query: {
      enabled: !isVX && isValidAddress,
    },
  });

  const { data: dataSimulateKongzVX } = useSimulateContract({
    ...getKongzVXContract(chain),
    functionName: 'transferFrom',
    args: [address!, sendTo as `0x${string}`, BigInt(id)],
    query: {
      enabled: isVX && chain?.type === 'mainnet' && isValidAddress,
    },
  });

  const { data: dataSimulateKongzVXPolygon } = useSimulateContract({
    ...getKongzVXPolygonContract(chain),
    functionName: 'transferFrom',
    args: [address!, sendTo as `0x${string}`, BigInt(id)],
    query: {
      enabled: isVX && chain?.type === 'polygon' && isValidAddress,
    },
  });

  const { txHash, writeContract } = useWriteContractWithTracking({
    dataSimulate: isVX
      ? chain?.type === 'mainnet'
        ? dataSimulateKongzVX
        : dataSimulateKongzVXPolygon
      : dataSimulateKongz,
    onInProgress: () => setStage('inProgress'),
    onCompleted: () => setStage('completed'),
  });

  function reset() {
    setStage('editing');
    setSendTo('');
  }

  return (
    <TransferActionFormView
      {...{
        stage,
        setStage,
        sendTo,
        setSendTo,
        id,
        type,
        isValidAddress,
        writeContract,
        reset,
        txHash,
        expectedNetwork,
      }}
    />
  );
}
